import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DaySkip {
  day: string;
  skips: number;
  meals?: string[]; // which meals were skipped on this day
}

interface MealSkipTrendsProps {
  data?: DaySkip[];
  avgSkips?: number;
}

const TEAL = "#20BCBB";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const meals: string[] = payload[0]?.payload?.meals ?? [];
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1 text-[10px] tracking-wide">
        {String(label).slice(0, 2)}
      </p>
      {meals.length > 0 ? (
        meals.map((m) => (
          <p key={m} className="text-[11px] text-[#1F1F1F] leading-tight">
            {m}
          </p>
        ))
      ) : (
        <p className="text-[11px] text-[#8E8E93] leading-tight">No skips</p>
      )}
    </div>
  );
};

function computeSkipYAxis(data: DaySkip[]): { domain: [number, number]; ticks: number[] } {
  const maxVal = data.reduce((m, d) => Math.max(m, d.skips ?? 0), 0);
  if (maxVal <= 0) return { domain: [0, 4], ticks: [0, 1, 2, 3, 4] };
  const step = Math.max(1, Math.ceil(maxVal / 4));
  const domainMax = step * 4;
  const ticks = Array.from({ length: 5 }, (_, i) => i * step);
  return { domain: [0, domainMax], ticks };
}

const MealSkipTrends: React.FC<MealSkipTrendsProps> = ({
  data = [],
  avgSkips,
}) => {
  const { domain, ticks } = computeSkipYAxis(data);
  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Meal Skip Trends
        </span>
      </div>

      {/* Chart */}
      <div className="flex-1">
        <ResponsiveContainer width="100%" height={160}>
          <LineChart
            data={data}
            margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          >
            <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              dy={6}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              domain={domain}
              ticks={ticks}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />

            <Line
              type="monotone"
              dataKey="skips"
              stroke={TEAL}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, fill: TEAL, strokeWidth: 2, stroke: "#FFFFFF" }}
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={1000}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Footer */}
      {avgSkips != null && (
        <div className="text-[12px] 3xl:text-[14px] text-[#8D8D8D] font-inter text-center mt-1">
          Avg skips this week :{" "}
          <span className="font-semibold text-[#1F1F1F]">{avgSkips} skips per day</span>
        </div>
      )}
    </div>
  );
};

export default MealSkipTrends;
